import {
  previousReportingPeriod,
  nextReportingPeriod,
  dateToWeeklyReportingPeriod,
  reportingPeriodToDate,
  endOfReportingPeriodToDate
} from '@fielded/fs-api/lib/tools'

import {
  format,
  differenceInCalendarDays,
  subDays,
  addDays,
  setDate,
  getMonth,
  setMonth,
  subMonths,
  addMonths,
  lastDayOfMonth,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  addQuarters,
  getYear,
  subYears,
  addYears
} from 'date-fns'
import { isPsmBasedApp } from '../van-shared/utils/utils'

export const PERIOD_DATE_FORMAT = 'yyyy-LL-dd'
export const PERIOD_MONTH_FORMAT = 'yyyy-LL'
// keep a full date here to avoid issues with month mixups
export const PERIOD_QUARTER_FORMAT = 'yyyy-LL-dd'
export const PERIOD_YEAR_FORMAT = 'yyyy'
export const PERIOD_DATE_CONNECTOR = '--'

export const PERIOD_CUSTOM = 'custom'
export const PERIOD_ISOWEEK = 'week'
export const PERIOD_BIMONTH = 'bimonth'
export const PERIOD_QUARTER = 'quarter'
export const PERIOD_MONTH = 'month'
export const PERIOD_YEAR = 'year'
export const PERIOD_BIWEEK = 'biweek'
export const PERIOD_DAY = 'day'

export const periodTypes = ['week', 'bimonth', 'month', 'quarter', 'year', 'custom', 'biweek', 'day']

const handleUnknownPeriodType = (periodType, callerName) => {
  console.warn(`Unknown period type in ${callerName}: ${periodType}`)
  return null
}

const reportingPeriods = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  BIMONTHLY: 'bimonthly',
  QUARTERLY: 'quarterly'
}

export const getPeriodTypeAndDate = (period) => {
  if (!period) return null
  let periodType
  let periodDate

  if (period.indexOf(':') !== -1) {
    [periodType, periodDate] = period.split(':')
  } else {
    // we assume that only a type has been passed
    // to get the default current/next/prev period
    periodType = period
  }

  if (!periodTypes.includes(periodType)) {
    return handleUnknownPeriodType(periodType, 'getPeriodTypeAndDate')
  }

  return {
    periodType,
    periodDate
  }
}

export const getCurrentPeriod = periodType => {
  const today = new Date().toISOString()

  switch (periodType) {
    case PERIOD_ISOWEEK:
      return formatPeriod(periodType, dateToWeeklyReportingPeriod('weekly'))
    case PERIOD_BIMONTH:
      return formatPeriod(periodType, previousReportingPeriod('bimonthly'))
    case PERIOD_BIWEEK:
      return formatPeriod(periodType, dateToWeeklyReportingPeriod('biweekly'))
    case PERIOD_QUARTER:
      return formatPeriod(periodType, subQuarters(today, 1))
    case PERIOD_DAY:
      return formatPeriod(periodType, today)
    case PERIOD_CUSTOM:
      console.warn(`Can not set a default period for period type custom`)
      return null
    default:
      return formatPeriod(periodType, today)
  }
}

export const getPrevPeriod = period => {
  if (!period) return null
  let { periodType, periodDate } = getPeriodTypeAndDate(period)

  if (!periodDate) {
    // if only a type was passed, we assume we should base this on today
    const { periodDate: defaultDate } = getPeriodTypeAndDate(getCurrentPeriod(periodType)) || {}
    periodDate = defaultDate
  }

  switch (periodType) {
    case PERIOD_CUSTOM:
      if (!periodDate) return null
      let { from, to } = periodToDateRange(period)
      from = removeTime(from)
      to = removeTime(to)
      const periodLength = differenceInCalendarDays(to, from)
      const prevTo = subDays(from, 1)
      const prevPeriod = {
        from: subDays(prevTo, periodLength),
        to: prevTo
      }
      return formatPeriod(periodType, prevPeriod)
    case PERIOD_YEAR:
      return formatPeriod(periodType, subYears(periodDate, 1))
    case PERIOD_QUARTER:
      return formatPeriod(periodType, subQuarters(periodDate, 1))
    case PERIOD_MONTH:
      return formatPeriod(periodType, subMonths(periodDate, 1))
    case PERIOD_DAY:
      return formatPeriod(periodType, subDays(periodDate, 1))
    case PERIOD_ISOWEEK:
      return formatPeriod(periodType, previousReportingPeriod('weekly', periodDate))
    case PERIOD_BIMONTH:
      return formatPeriod(periodType, previousReportingPeriod('bimonthly', periodDate))
    case PERIOD_BIWEEK:
      return formatPeriod(periodType, previousReportingPeriod('biweekly', periodDate))
    default:
      handleUnknownPeriodType(periodType, 'getPrevPeriod')
  }
}

export const getNextPeriod = period => {
  if (!period) return null
  let { periodType, periodDate } = getPeriodTypeAndDate(period)

  if (!periodDate) {
    // if only a type was set, we assume we should base this on today
    const { periodDate: defaultDate } = getPeriodTypeAndDate(getCurrentPeriod(periodType)) || {}
    periodDate = defaultDate
  }

  switch (periodType) {
    case PERIOD_CUSTOM:
      if (!periodDate) return null
      let { from, to } = periodToDateRange(period)
      from = removeTime(from)
      to = removeTime(to)
      const periodLength = differenceInCalendarDays(to, from)
      const nextFrom = addDays(to, 1)
      const nextPeriod = {
        from: nextFrom,
        to: addDays(nextFrom, periodLength)
      }
      return formatPeriod(periodType, nextPeriod)
    case PERIOD_YEAR:
      return formatPeriod(periodType, addYears(periodDate, 1))
    case PERIOD_QUARTER:
      return formatPeriod(periodType, addQuarters(periodDate, 1))
    case PERIOD_MONTH:
      return formatPeriod(periodType, addMonths(periodDate, 1))
    case PERIOD_DAY:
      return formatPeriod(periodType, addDays(periodDate, 1))
    case PERIOD_ISOWEEK:
      return formatPeriod(periodType, nextReportingPeriod('weekly', periodDate))
    case PERIOD_BIMONTH:
      return formatPeriod(periodType, nextReportingPeriod('bimonthly', periodDate))
    case PERIOD_BIWEEK:
      return formatPeriod(periodType, nextReportingPeriod('biweekly', periodDate))
    default:
      handleUnknownPeriodType(periodType, 'getNextPeriod')
  }
}

export const formatPeriod = (periodType, periodDate) => {
  if (!periodDate) return null

  if (!periodType || !periodTypes.includes(periodType)) {
    handleUnknownPeriodType(periodType, 'formatPeriod')
  }

  if (typeof periodDate === 'object' && periodType === PERIOD_CUSTOM) {
    periodDate = Object.keys(periodDate).map(key => {
      const date = periodDate[key]
      return format(date, PERIOD_DATE_FORMAT)
    }).join(PERIOD_DATE_CONNECTOR)
  }

  if (periodType === PERIOD_MONTH) {
    periodDate = format(periodDate, PERIOD_MONTH_FORMAT)
  }

  if (periodType === PERIOD_QUARTER) {
    periodDate = format(periodDate, PERIOD_QUARTER_FORMAT)
  }

  if (periodType === PERIOD_YEAR) {
    periodDate = format(periodDate, PERIOD_YEAR_FORMAT)
  }

  if (periodType === PERIOD_DAY) {
    periodDate = format(periodDate, PERIOD_DATE_FORMAT)
  }

  return `${periodType}:${periodDate}`
}

export const removeTime = jsonDate => {
  // get the Day from the JSON date string.
  // e.g if we have 2019-05-26T23:00:00Z it'll give us `Mon May 27 2019` in GMT +1
  return new Date(jsonDate).toDateString()
}

export const periodToHumanReadable = period => {
  const { periodType, periodDate } = getPeriodTypeAndDate(period)

  switch (periodType) {
    case PERIOD_CUSTOM:
    case PERIOD_ISOWEEK: {
      const dates = periodToDateRange(period)
      return dateRangeToHumanReadable({
        to: removeTime(dates.to),
        from: removeTime(dates.from)
      })
    }

    case PERIOD_DAY:
      return format(periodDate, 'd LLLL yyyy')

    case PERIOD_YEAR:
      return format(periodDate, 'yyyy')

    case PERIOD_QUARTER:
      return format(periodDate, 'QQQ yyyy')

    case PERIOD_MONTH:
      return format(periodDate, 'LLLL yyyy')

    case PERIOD_BIMONTH: {
      const date = reportingPeriodToDate(periodDate)
      const firstMonth = format(date, 'LLL')
      const secondMonth = format(addMonths(date, 1), 'LLL yyyy')
      return `${firstMonth}-${secondMonth}`
    }

    default:
      handleUnknownPeriodType(periodType, 'periodToHumanReadable')
  }
}

/*
 * We do this to be consistent across time zones
 */
export const beginPeriod = date => {
  date.setHours(0, 0, 0, 0)
  return date.toISOString()
}

export const endPeriod = date => {
  date.setHours(23, 59, 59, 999)
  return date.toISOString()
}

export const periodToDateRange = period => {
  if (!period) return null

  const { periodType, periodDate } = getPeriodTypeAndDate(period)
  let startDate
  let endDate

  switch (periodType) {
    case PERIOD_CUSTOM:
      if (periodDate.includes(PERIOD_DATE_CONNECTOR)) {
        const [from, to] = periodDate
          .split(PERIOD_DATE_CONNECTOR)
          .sort((a, b) => (new Date(b) - new Date(a)) * -1)
        startDate = from
        endDate = to
      }

      return {
        from: beginPeriod(new Date(startDate)),
        to: endPeriod(new Date(endDate))
      }

    case PERIOD_YEAR:
      startDate = setDate(setMonth(periodDate, 0), 1)
      endDate = setDate(setMonth(periodDate, 11), 31)

      return {
        from: beginPeriod(startDate),
        to: endPeriod(endDate)
      }

    case PERIOD_QUARTER:
      startDate = startOfQuarter(periodDate)
      endDate = endOfQuarter(periodDate)

      return {
        from: beginPeriod(startDate),
        to: endPeriod(endDate)
      }

    case PERIOD_MONTH:
      // Normalize period string to a valid YYYY-MM format
      const parsedDate = periodDate.replace('M', '')
      startDate = setDate(parsedDate, 1)
      endDate = lastDayOfMonth(parsedDate)

      return {
        from: beginPeriod(startDate),
        to: endPeriod(endDate)
      }
    case PERIOD_DAY:
      startDate = new Date(periodDate)
      endDate = new Date(periodDate)

      return {
        from: beginPeriod(startDate),
        to: endPeriod(endDate)
      }
    case PERIOD_ISOWEEK:
      const prevPeriod = previousReportingPeriod('weekly', periodDate)
      const prevPeriodEnd = endOfReportingPeriodToDate(prevPeriod)
      startDate = addDays(prevPeriodEnd, 1)
      endDate = new Date(endOfReportingPeriodToDate(periodDate))

      return {
        from: beginPeriod(startDate),
        to: endPeriod(endDate)
      }
    case PERIOD_BIMONTH:
      startDate = new Date(reportingPeriodToDate(periodDate))
      startDate.setHours(0, 0, 0, 0)
      endDate = new Date(endOfReportingPeriodToDate(periodDate))

      return {
        from: beginPeriod(startDate),
        to: endPeriod(endDate)
      }
    default:
      handleUnknownPeriodType(periodType, 'periodToDateRange')
  }
}

export const periodToDateRangeNoTime = period => {
  const range = periodToDateRange(period)
  return {
    to: removeTime(range.to),
    from: removeTime(range.from)
  }
}

export const dateRangeToHumanReadable = dateRange => {
  // only show date info as needed
  // 1-4 Dec 2018 or even just 1-4 Dec '18
  // 29 Nov - 3 Dec 2018
  // 28 Dec 2018 - 04 Jan 2019
  const { from, to } = dateRange

  const isSameYear = getYear(from) === getYear(to)
  const isSameMonth = getMonth(from) === getMonth(to)

  let fromDisplay = format(from, 'd LLL yyyy')
  let toDisplay = format(to, 'd LLL yyyy')
  let connector = ' - '

  if (isSameYear) {
    if (isSameMonth) {
      fromDisplay = format(from, 'd')
      connector = '-'
    } else {
      fromDisplay = format(from, 'd LLL')
    }
  }

  return `${fromDisplay}${connector}${toDisplay}`
}

export const getDefaultPeriod = (config, programs) => {
  const isPsmBased = isPsmBasedApp(config)
  const app = config.app

  if (isPsmBased) {
    const isWeeklyProgram = () => {
      return programs && programs.every(program => program.reportingPeriod === reportingPeriods.WEEKLY)
    }

    const isDailyProgram = () => {
      return programs && programs.every(program => program.reportingPeriod === reportingPeriods.DAILY)
    }

    const isMonthlyProgram = () => {
      return programs && programs.every(program => program.reportingPeriod === reportingPeriods.MONTHLY)
    }

    const isQuarterlyProgram = () => {
      return programs && programs.every(program => program.reportingPeriod === reportingPeriods.QUARTERLY)
    }

    if (isQuarterlyProgram()) {
      return getCurrentPeriod(PERIOD_QUARTER)
    }

    if (isWeeklyProgram()) {
      return getCurrentPeriod(PERIOD_ISOWEEK)
    }

    if (isMonthlyProgram()) {
      return getCurrentPeriod(PERIOD_MONTH)
    }

    if (isDailyProgram()) {
      return getCurrentPeriod(PERIOD_DAY)
    }

    return getCurrentPeriod(PERIOD_BIMONTH)
  }

  if (app === 'van') {
    return getCurrentPeriod(PERIOD_ISOWEEK)
  }

  console.log('No default period set for app: ', app, 'default to VAN settings')
  return getCurrentPeriod(PERIOD_ISOWEEK)
}

export const isTodayInReportingPeriod = period => {
  return isDateInPeriodCycle(new Date().toISOString(), period)
}

export const isDateInPeriodCycle = (date, period) => {
  try {
    const [periodType, periodDate] = period.split(':')
    switch (periodType) {
      case PERIOD_ISOWEEK:
        return periodDate === dateToWeeklyReportingPeriod('weekly', date)
      case PERIOD_BIMONTH:
        // for psm bimonthly always allow them to edit
        // https://github.com/fielded/van-orga/issues/1833#issuecomment-401334988
        return true
      default:
        throw new Error('Unknown period type: ' + periodType)
    }
  } catch (e) {
    throw new Error('Date or period is incorrect', e.message)
  }
}
